import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Stack,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  getFanById,
  addFan,
  saveFan,
  getFanBoyutlari,
  getFanGucleri,
  getFanKonumlari,
} from "../services/api";

const ORANGE_COLOR = "#FF7A2B";
const ORANGE_HOVER_COLOR = "#E66A20";

const FanModal = ({ open, onClose, onSave, kumesId, fanId }) => {
  const [formData, setFormData] = useState({
    FAN_NO: "",
    YERDEN_YUKSEKLIK_CM: "",
    FAN_KONUM_ID: "",
    FAN_BOYUT_ID: "",
    FAN_GUC_ID: "",
  });

  const [boyutlar, setBoyutlar] = useState([]);
  const [gucler, setGucler] = useState([]);
  const [konumlar, setKonumlar] = useState([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const isEditMode = Boolean(fanId);
  const formComponentStyles = {
    "& .MuiInputLabel-root.Mui-focused": { color: ORANGE_COLOR },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: ORANGE_COLOR,
      },
    },
  };

  useEffect(() => {
    const loadInitialData = async () => {
      if (!open) return;
      setLoading(true);

      setFormData({
        FAN_NO: "",
        YERDEN_YUKSEKLIK_CM: "",
        FAN_KONUM_ID: "",
        FAN_BOYUT_ID: "",
        FAN_GUC_ID: "",
      });

      try {
        const [boyutRes, gucRes, konumRes] = await Promise.all([
          getFanBoyutlari(),
          getFanGucleri(),
          getFanKonumlari(),
        ]);

        const boyutData = boyutRes.data?.data || [];
        const gucData = gucRes.data?.data || [];
        const konumData = konumRes.data?.data || [];

        setBoyutlar(boyutData);
        setGucler(gucData);
        setKonumlar(konumData);

        if (isEditMode) {
          const fanRes = await getFanById(fanId);
          const fanData = fanRes.data?.data?.[0] || {};

          // console.log("DÜZENLEME İÇİN GELEN FAN VERİSİ:", fanData);

          setFormData({
            FAN_NO: fanData.FAN_NO || "",
            YERDEN_YUKSEKLIK_CM: fanData.YERDEN_YUKSEKLIK_CM || "",
            FAN_KONUM_ID: fanData.KONUM_ID ? String(fanData.KONUM_ID) : "",
            FAN_BOYUT_ID: fanData.BOYUT_ID ? String(fanData.BOYUT_ID) : "",
            FAN_GUC_ID: fanData.GUC_ID ? String(fanData.GUC_ID) : "",
          });
        }
      } catch (err) {
        enqueueSnackbar(`Dropdown verileri yüklenemedi: ${err.message}`, {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [open, fanId, isEditMode, enqueueSnackbar]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (
      !formData.FAN_NO ||
      !formData.YERDEN_YUKSEKLIK_CM ||
      !formData.FAN_KONUM_ID ||
      !formData.FAN_BOYUT_ID ||
      !formData.FAN_GUC_ID
    ) {
      enqueueSnackbar("Lütfen tüm zorunlu alanları doldurun.", {
        variant: "warning",
      });
      return;
    }
    setLoading(true);

    try {
      const payload = {
        KUMES_ID: kumesId,
        ...formData,
      };

      if (isEditMode) {
        await saveFan({ ...payload, KUMES_FAN_ID: fanId });
        enqueueSnackbar("Fan başarıyla güncellendi.", { variant: "success" });
      } else {
        await addFan(payload);
        enqueueSnackbar("Yeni fan başarıyla eklendi.", { variant: "success" });
      }
      onSave();
      onClose();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Bilinmeyen bir hata oluştu.";
      enqueueSnackbar(`İşlem sırasında hata: ${errorMessage}`, {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      disableRestoreFocus
    >
      <DialogTitle>{isEditMode ? "Fan Düzenle" : "Yeni Fan Ekle"}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress sx={{ color: ORANGE_COLOR }} />
          </Box>
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1.5 }}>
            <TextField
              fullWidth
              label="Fan Numarası"
              name="FAN_NO"
              value={formData.FAN_NO}
              onChange={handleChange}
              required
              sx={formComponentStyles}
            />
            <TextField
              fullWidth
              label="Yerden Yükseklik (cm)"
              name="YERDEN_YUKSEKLIK_CM"
              type="number"
              value={formData.YERDEN_YUKSEKLIK_CM}
              onChange={handleChange}
              required
              sx={formComponentStyles}
            />
            <FormControl fullWidth required sx={formComponentStyles}>
              <InputLabel>Fan Konumu</InputLabel>
              <Select
                name="FAN_KONUM_ID"
                value={formData.FAN_KONUM_ID}
                label="Fan Konumu"
                onChange={handleChange}
              >
                {konumlar.map((konum) => (
                  <MenuItem key={konum.KONUM_ID} value={String(konum.KONUM_ID)}>
                    {konum.KONUM_ADI}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required sx={formComponentStyles}>
              <InputLabel>Fan Boyutu</InputLabel>
              <Select
                name="FAN_BOYUT_ID"
                value={formData.FAN_BOYUT_ID}
                label="Fan Boyutu"
                onChange={handleChange}
              >
                {boyutlar.map((boyut) => (
                  <MenuItem key={boyut.BOYUT_ID} value={String(boyut.BOYUT_ID)}>
                    {boyut.BOYUT_ADI}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required sx={formComponentStyles}>
              <InputLabel>Fan Gücü</InputLabel>
              <Select
                name="FAN_GUC_ID"
                value={formData.FAN_GUC_ID}
                label="Fan Gücü"
                onChange={handleChange}
              >
                {gucler.map((guc) => (
                  <MenuItem key={guc.GUC_ID} value={String(guc.GUC_ID)}>
                    {guc.GUC_ADI}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ p: "16px 24px" }}>
        <Button onClick={onClose} sx={{ color: ORANGE_COLOR }}>
          İptal
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: ORANGE_COLOR,
            "&:hover": { backgroundColor: ORANGE_HOVER_COLOR },
            position: "relative",
          }}
        >
          {loading ? (
            <CircularProgress
              size={24}
              sx={{ color: "white", position: "absolute" }}
            />
          ) : (
            "Kaydet"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FanModal;
